/**
 * What a game mode is.
 *
 * The rule this whole design exists to enforce is the user's own: **do not
 * build four separate games.** Physics, weapons, enemies, damage, upgrades,
 * vehicles, cargo, economy and terrain are shared and unconditional. A mode
 * changes what is *placed* in the world and what the *rules* around it are —
 * nothing else.
 *
 * Those are the two halves here:
 *
 *   `ModeContent`  what the world is BUILT from. Every field is exactly the
 *                  shape `level.ts` already exports, referenced by identity
 *                  rather than copied, so a change to the level data still
 *                  flows into the campaign and the two can never drift.
 *
 *   `ModeRules`    what happens while it runs. The campaign's entire
 *                  mode-specific behaviour is nine statements in
 *                  `World.update` — discoveries, hints, station arrival, five
 *                  onboarding triggers and the finale check. That is the whole
 *                  seam, which is why there is no phase registry here and no
 *                  restructuring of the update loop: the loop is already
 *                  universal simulation with nine campaign statements in the
 *                  middle of it.
 *
 * An empty array is legal everywhere and is how a mode declines a campaign
 * feature. Every consumer of these lists is a `for` loop, so "no stations" and
 * "no onboarding hints" need no conditional anywhere in `game/` — they are just
 * loops over nothing.
 */

import {
  BAIT_BALLS, ENEMY_ZONES, EVENT_HINTS, FINALE, HAZARDS, HINTS, OCTOPUS, PICKUPS,
  SPAWN, STATIONS,
  type BaitSpecData, type EnemyZone, type Hazard, type Hint, type PickupSpec,
  type Poi, type StationSpec,
} from './level';
import { POIS } from './level';
import { WORLD } from './config';
// Type-only, so this does not create a runtime cycle with world.ts.
import type { World } from './world';

export type ModeId = 'campaign' | 'survival' | 'expedition';

/**
 * The rules that run over the content.
 *
 * Deliberately small. The campaign's entire mode-specific behaviour is nine
 * statements, so anything larger than this would be scaffolding for its own
 * sake — and every extra method here is a method the wave director has to
 * implement before it can spawn its first enemy.
 *
 * It grows as modes actually need it to: `onCraftLost`, `award`, `services` and
 * an objective arrive when Survival needs them to differ, not before.
 */
export interface ModeRules {
  readonly id: ModeId;
  /** Shown on the mode-select card. */
  readonly label: string;
  readonly blurb: string;
  readonly content: ModeContent;
  /**
   * Whether this mode writes to the run save and accumulates spendable salvage.
   *
   * `false` is the enforcement for the PRD's §24 rule that temporary power must
   * not leave the run. It disarms `World.save()` outright — every path that
   * writes SaveData goes through it — and routes every `salvage +=` through a
   * purse that simply declines. There is no serialiser for a run modifier and
   * no currency to buy one with; the absence is the mechanism.
   */
  readonly persists: boolean;
  /** Called once when the run starts, after the world is built. */
  begin?(world: World): void;
  /** Called once a frame, in place of the campaign block that used to be inline. */
  tick(world: World, dt: number): void;
  /** True when the run is over and the shell should show its ending. */
  ended?(world: World): boolean;
  /** Wave number for the HUD, or 0 when the mode has no waves. */
  wave?(): number;
  /**
   * Where the HUD's objective chevron should point. Null falls back to the
   * campaign's "nearest station you have not activated yet".
   */
  objective?(world: World): { x: number; y: number; label: string } | null;
  /** A load was just sold at a station. Expedition settles contracts here. */
  onDeliver?(world: World, id: string, paid: number): void;
  /**
   * A standing readout for the HUD: what the player is supposed to be doing
   * right now, and it has to be on screen the whole time.
   *
   * Expedition announced its contract in a toast and then showed nothing, so
   * three seconds after taking a job there was no way to find out what the job
   * WAS. A mode whose content is an instruction has to keep the instruction
   * visible.
   *
   * This used to say the campaign did not need one, because its instruction is
   * "down". That was wrong, and reported as wrong: "down" is the SHAPE of the
   * campaign, not the task in front of you. What the player actually needs is
   * which station is next and how far — which existed, in eight-pixel type
   * pinned to the edge of the screen behind a chevron, and could not be read.
   *
   * `headline` is the one figure worth reading at a glance. It gets the big
   * type; everything else is context around it. A panel where every line is the
   * same size has no information design in it at all — it is a paragraph, and a
   * paragraph is not something anyone reads while being shot at.
   */
  status?(world: World): {
    title: string;
    lines: string[];
    headline?: string;
    headlineLabel?: string;
  } | null;
}

export interface ModeContent {
  readonly zones: readonly EnemyZone[];
  readonly pickups: readonly PickupSpec[];
  readonly stations: readonly StationSpec[];
  readonly baitBalls: readonly BaitSpecData[];
  readonly hazards: readonly Hazard[];
  readonly hints: readonly Hint[];
  readonly eventHints: Readonly<Record<string, { text: string; key: string; touch?: string }>>;
  readonly pois: readonly Poi[];
  /**
   * Drifting jellies and blooms across this span. Replaces the two `x < 7900`
   * literals that used to be written into the spawn loops. `null` means none.
   */
  readonly ambient: { x0: number; x1: number } | null;
  readonly octopus: { x: number; y: number; lairX: number; lairY: number } | null;
  readonly spawn: { x: number; y: number };
  readonly finale: { x: number; y: number; radius: number } | null;
  /**
   * Soft play bounds, enforced as a position/velocity rule in `tick`.
   *
   * Explicitly NOT terrain and NOT a current: `world.hazards` applies an
   * acceleration capped at `h.power`, and the strongest current authored in the
   * game is 16 against 62–140 m/s² of thrust. A current cannot fence anything;
   * a player under nitro leaves whenever they like.
   */
  readonly bounds: { x0: number; x1: number; y0: number; y1: number } | null;
  /** The x the chart strip normalises against. */
  readonly endX: number;
}

/**
 * The 7.9 km descent, by reference.
 *
 * Nothing here is copied. `scripts/mode-parity.mjs` asserts
 * `CAMPAIGN_CONTENT.zones === ENEMY_ZONES` by identity precisely so that nobody
 * can later "tidy" this into a spread and quietly fork the campaign from the
 * level data it is supposed to be.
 */
export const CAMPAIGN_CONTENT: ModeContent = {
  zones: ENEMY_ZONES,
  pickups: PICKUPS,
  stations: STATIONS,
  baitBalls: BAIT_BALLS,
  hazards: HAZARDS,
  hints: HINTS,
  eventHints: EVENT_HINTS,
  pois: POIS,
  // The literals that used to live in `spawnJellies` and `spawnBlooms`.
  // Reaches the western zones. It was 120..7900 — the spawn eastward — so the
  // coral garden and the meadow got ZERO jellies and ZERO blooms, which is most
  // of why the reef read as empty: it had scenery and almost nothing alive in
  // the water above it. The two prettiest animals in the game were excluded
  // from the zone built to be looked at.
  ambient: { x0: WORLD.startX + 120, x1: 7900 },
  octopus: OCTOPUS,
  spawn: SPAWN,
  finale: FINALE,
  bounds: null,
  endX: WORLD.endX,
};

/**
 * Expedition: the campaign's water, with the ending taken out.
 *
 * Every field is `CAMPAIGN_CONTENT`'s, by reference — same enemies, same
 * stations, same shoals, same hazards — because the settled decision was that
 * the 7.9 km descent is the water every mode happens in, and a second copy of
 * it would be a second game to keep in sync.
 *
 * The one difference is `finale: null`, and it is the whole mode. The campaign
 * is a destination and stops when you reach it; an expedition is a job you take
 * again. Leaving the finale in would end a mode that is not supposed to end,
 * with a victory card for something the player was not doing.
 */
export const EXPEDITION_CONTENT: ModeContent = {
  ...CAMPAIGN_CONTENT,
  finale: null,
};

/**
 * Survival's arena: 260 m of the coastal shelf, x140–400.
 *
 * The location is the design. Measured, its terrain band is 106–113 m per chunk
 * against the trench's 438 and the kelp shelf's 500; its chunks mesh at 5.5–8.6k
 * triangles against the grotto's 10.2k; and it is OPEN WATER, so `fsTerrain`
 * shades 43–50 % of the screen rather than the 84–100 % an enclosed room costs.
 * A bounded pit would have been the obvious choice and it is the expensive one:
 * `detailBand` grows to the tallest feature in a column, which is exactly what
 * made the trench cost what it does.
 *
 * It also adds ZERO features to the authored world, so `field.ts`, the flood
 * fill, the vault seals and the economy audit are all untouched by its
 * existence.
 */
export const SURVIVAL_CONTENT: ModeContent = {
  zones: [],            // every enemy is a runtime spawn
  pickups: [],
  // One station, for the intermission's repair. It is never docked at through
  // `World.dock`, which would try to save.
  stations: [{ id: 's-arena', x: 270, y: -44, name: 'ARENA MOORING' }],
  // NO bait ball. It was the campaign's first shoal by identity — 320 fish,
  // meant as something alive in the water between waves — and in a mode whose
  // horde is partly made of fish it was actively harmful: the player could not
  // tell an enemy swarmer from a decorative one, so the thing you are supposed
  // to be shooting was camouflaged by scenery. It also orbits as a flat ring,
  // which reads badly in a fight rather than as the bait ball it is in the open
  // world. The campaign keeps it; the arena is water and enemies.
  baitBalls: [],
  hazards: [],
  hints: [],
  eventHints: {},
  pois: [],
  ambient: null,        // no 7.9 km of jellies and blooms to build at load
  // Its own lair, 24 m inside the seabed, so the boss reaches the whole column.
  octopus: { x: 270, y: -70, lairX: 290, lairY: -78 },
  spawn: { x: 270, y: -34 },
  finale: null,
  bounds: { x0: 140, x1: 400, y0: -72, y1: 0 },
  endX: 400,
};
